import { useEffect } from "react";
import { numToCurrency, decimToPercent } from "../utils/transformators";
import * as Calculations from "../utils/calculations";
import useSPQS from "../../../hooks/useSPQS";
import moment from "moment";

const useCalculationsSP = () => {
  // Load context values
  const { SPQSData, setSPQS, setSPQSData, ppaymenttermDataSPQS, spaymenttermDataSPQS, whdataSP, setWhdataSP } =
    useSPQS();

  useEffect(() => {
    const docalcs = async () => {
      // totalinboundcalc
      const ttloutbound = await Calculations.sumvalues(
        SPQSData.outdrayage,
        SPQSData.loading,
        SPQSData.outcollectcharges,
        SPQSData.outboundothers
      );
      // outboundpmtcalc
      const outbpmt = await Calculations.dividevalues(ttloutbound, SPQSData.outpayload);
      // freightpmtcalc
      const frtpmt = await Calculations.dividevalues(SPQSData.oceanfreight, SPQSData.oceanpayload);
      //  materialvaluecalc
      const matvalue = await Calculations.multiplyvalues(SPQSData.quantity, SPQSData.materialcost);
      // storagecost
      let stgcost;
      if (SPQSData.qstype == 2) {
        const daysInStorage = Math.max(
          moment(SPQSData.whexit, "YYYY-MM-DD").diff(moment(whdataSP?.whentrydate, "YYYY-MM-DD"), "days") -
            whdataSP?.whgraceperiod,
          0
        );
        setWhdataSP((prev) => prev && { ...prev, ["daysinstorage"]: daysInStorage });
        stgcost = await Calculations.storagepmtcalc(
          daysInStorage,
          whdataSP?.whfixedcost,
          whdataSP?.whvarcost,
          whdataSP?.quantity,
          whdataSP?.numpallets,
          whdataSP?.whaccrualperiod
        );
        // console.log(stgcost);
      } else {
        stgcost = 0;
      }
      // totaldutycalc
      const ttlduty = await Calculations.sumvalues(SPQSData.generaldutyimp, SPQSData.additionaldutyimp);
      // dutyfeecalc
      const dtyfee = await Calculations.multiplyvalues(matvalue, ttlduty);
      // harborfeecalc
      const hbrfee = await Calculations.multiplyvalues(matvalue, SPQSData.harborfeepct);
      // merchprocfeecalc
      const mrcfee = await Calculations.multiplyvalues(matvalue, SPQSData.merchprocfeepct);
      // totalcentryfeecalc
      const ttlcentryfee = await Calculations.sumvalues(
        dtyfee,
        hbrfee,
        mrcfee,
        SPQSData.flatfee,
        SPQSData.tsca,
        SPQSData.isf
      );
      // centryfeepmtcalc
      const centrypmt = await Calculations.dividevalues(ttlcentryfee, SPQSData.quantity);
      // totalinboundcalc
      const ttlinbound = await Calculations.sumvalues(
        SPQSData.indrayage,
        SPQSData.unloading,
        SPQSData.incollectcharges,
        SPQSData.inboundothers
      );
      // inboundpmtcalc
      const inbpmt = await Calculations.dividevalues(ttlinbound, SPQSData.inpayload);
      // totalcostcalc
      const ttlcost = await Calculations.sumvalues(
        SPQSData.materialcost,
        stgcost || 0,
        outbpmt,
        centrypmt,
        inbpmt,
        SPQSData.pcomission,
        SPQSData.pfinancecost,
        SPQSData.scomission,
        SPQSData.sfinancecost,
        frtpmt,
        SPQSData.inspectioncost,
        SPQSData.legal,
        SPQSData.pallets,
        SPQSData.other
      );
      // insurcalc
      const inscost = await Calculations.multiplyvalues(ttlcost, SPQSData.insurancerate, SPQSData.insurancefactor);
      // interestcostcalc
      const intercost = await Calculations.interestcostcalc(
        ttlcost,
        ppaymenttermDataSPQS,
        spaymenttermDataSPQS,
        SPQSData.pinterest,
        SPQSData.sinterest,
        SPQSData.salesprice
      );
      // finalpricecalc
      const paii = await Calculations.sumvalues(ttlcost, intercost, inscost);
      // profitcalc
      const profit = SPQSData.quantity && (await Calculations.subvalues(SPQSData.salesprice, paii));
      // totalprofitcalc
      const totalprofit = profit * SPQSData.quantity;
      // turnovercalc
      const trnover = SPQSData.quantity && (await Calculations.multiplyvalues(SPQSData.salesprice, SPQSData.quantity));
      // margincalc
      const mrgin = trnover > 0 ? await Calculations.dividevalues(totalprofit, trnover) : 0;
      // netbackcalc
      const ntback = await Calculations.sumvalues(profit, SPQSData.materialcost);

      setSPQS((prev) => ({
        ...prev,
        ttloutbound: numToCurrency(ttloutbound),
        outboundcost: numToCurrency(outbpmt),
        freightpmt: numToCurrency(frtpmt),
        materialvalue: numToCurrency(matvalue),
        ttlduty: decimToPercent(ttlduty),
        dutyfee: numToCurrency(dtyfee),
        harborfee: numToCurrency(hbrfee),
        merchprocfee: numToCurrency(mrcfee),
        ttlcentryfee: numToCurrency(ttlcentryfee),
        importcustomsfee: numToCurrency(centrypmt),
        inboundcost: numToCurrency(inbpmt),
        totalcost: numToCurrency(ttlcost),
        insurancecost: numToCurrency(inscost),
        interestcost: numToCurrency(intercost),
        totallandedcost: numToCurrency(paii),
        profitpmt: numToCurrency(profit),
        profittotal: numToCurrency(totalprofit),
        turnover: numToCurrency(trnover),
        margin: decimToPercent(mrgin),
        netback: numToCurrency(ntback),
        storagecost: numToCurrency(stgcost),
        // materialvalue: numToCurrency(matvalue),
      }));
      setSPQSData((prev) => ({
        ...prev,
        ttloutbound: ttloutbound,
        outboundcost: outbpmt,
        freightpmt: frtpmt,
        materialvalue: matvalue,
        ttlduty: ttlduty,
        dutyfee: dtyfee,
        harborfee: hbrfee,
        merchprocfee: mrcfee,
        ttlcentryfee: ttlcentryfee,
        importcustomsfee: centrypmt,
        inboundcost: inbpmt,
        totalcost: ttlcost,
        insurancecost: inscost,
        interestcost: intercost,
        totallandedcost: paii,
        profitpmt: profit,
        profittotal: totalprofit,
        turnover: trnover,
        margin: mrgin,
        netback: ntback,
        // materialvalue: matvalue,
      }));
    };
    docalcs();
  }, [
    SPQSData.outdrayage,
    SPQSData.loading,
    SPQSData.outcollectcharges,
    SPQSData.outboundothers,
    SPQSData.outpayload,
    SPQSData.oceanfreight,
    SPQSData.oceanpayload,
    SPQSData.quantity,
    SPQSData.materialcost,
    SPQSData.generaldutyimp,
    SPQSData.additionaldutyimp,
    SPQSData.harborfeepct,
    SPQSData.merchprocfeepct,
    SPQSData.flatfee,
    SPQSData.tsca,
    SPQSData.isf,
    SPQSData.inpayload,
    SPQSData.indrayage,
    SPQSData.unloading,
    SPQSData.incollectcharges,
    SPQSData.inboundothers,
    SPQSData.pcomission,
    SPQSData.pfinancecost,
    SPQSData.scomission,
    SPQSData.sfinancecost,
    SPQSData.inspectioncost,
    SPQSData.legal,
    SPQSData.pallets,
    SPQSData.other,
    SPQSData.insurancerate,
    SPQSData.insurancefactor,
    SPQSData.pinterest,
    SPQSData.sinterest,
    ppaymenttermDataSPQS,
    spaymenttermDataSPQS,
    SPQSData.salesprice,
    SPQSData.whexit,
    SPQSData.numpallets,
    whdataSP?.whfixedcost,
    whdataSP?.whvarcost,
    whdataSP?.quantity,
    whdataSP?.numpallets,
    whdataSP?.whaccrualperiod,
    whdataSP?.whentrydate,
  ]);
};

export default useCalculationsSP;
